import { useState } from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'

import { Calendar } from '@/components/ui/calendar'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@/utils/cn'
import { inputClass, labelClass } from './shared/modalStyles'

interface ExpiryPickerProps {
  date: Date | undefined
  time: string
  onDateChange: (date: Date | undefined) => void
  onTimeChange: (time: string) => void
}

const ExpiryPicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
}: ExpiryPickerProps) => {
  const [open, setOpen] = useState(false)
  const minTime =
    date && format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
      ? format(new Date(), 'HH:mm')
      : undefined

  return (
    <div>
      <label className={labelClass}>Expiry Date &amp; Time</label>
      {/* No Portal — keeps calendar inside Dialog.Content so Radix focus-trap doesn't block clicks */}
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type='button'
            className={cn(
              inputClass,
              'flex items-center gap-2 text-left',
              !date && 'text-text-tertiary',
            )}
          >
            <CalendarIcon className='text-text-tertiary h-4 w-4 shrink-0' />
            {date
              ? `${format(date, 'dd MMM yyyy')} · ${time}`
              : 'Pick expiry date & time'}
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Content
          align='start'
          sideOffset={4}
          className='z-50 w-auto rounded-lg border border-zinc-700 bg-zinc-900 p-0 shadow-xl outline-none'
        >
          <Calendar
            mode='single'
            selected={date}
            onSelect={(d) => {
              onDateChange(d)
            }}
            disabled={{ before: new Date() }}
            initialFocus
          />
          <div className='border-border-main border-t px-3 pt-2 pb-3'>
            <label className='text-text-tertiary mb-1 block text-xs'>
              Time
            </label>
            <input
              type='time'
              value={time}
              min={minTime}
              onChange={(e) => onTimeChange(e.target.value)}
              className='border-border-main bg-neutral-dimmed text-text-primary w-full rounded-md border px-3 py-1.5 text-sm focus:border-violet-500 focus:outline-none'
            />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Root>
      {date && (
        <button
          type='button'
          onClick={() => {
            onDateChange(undefined)
            onTimeChange('23:59')
          }}
          className='text-text-tertiary hover:text-text-secondary mt-1.5 text-xs'
        >
          Clear expiry
        </button>
      )}
    </div>
  )
}

export default ExpiryPicker
